import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supportAPI } from '../../services/supportAPI';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { CheckCircle, Clock, Send, Paperclip, X, AlertCircle, RefreshCw, FileText, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
    'Open': 'bg-blue-100 text-blue-800 border-blue-200',
    'Assigned': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    'In Progress': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Waiting for Customer': 'bg-orange-100 text-orange-800 border-orange-200',
    'Resolved': 'bg-green-100 text-green-800 border-green-200',
    'Closed': 'bg-gray-100 text-gray-800 border-gray-200',
    'Reopened': 'bg-purple-100 text-purple-800 border-purple-200',
};

const getAssetUrl = (path) => {
    if (!path) return '';
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace('/api', '');
    return baseUrl + path;
};

const AttachmentViewer = ({ url, isCustomer }) => {
    const isImage = url.match(/\.(jpeg|jpg|gif|png|webp)$/i) != null;
    const fullUrl = getAssetUrl(url);
    const fileName = url.split('/').pop() || 'Document';

    if (isImage) {
        return (
            <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="block mt-2 overflow-hidden rounded-lg border border-midnight-100 hover:opacity-90 transition-opacity">
                <img src={fullUrl} alt="Attachment" className="max-w-full max-h-48 object-cover" />
            </a>
        );
    }

    return (
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" 
           className={`flex items-center gap-3 p-3 mt-2 rounded-xl border ${isCustomer ? 'bg-white/10 border-white/20 hover:bg-white/20 text-white' : 'bg-midnight-50 border-midnight-100 hover:bg-midnight-100 text-midnight-800'} transition-colors max-w-sm`}>
            <div className={`p-2 rounded-lg ${isCustomer ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                <FileText className="w-6 h-6" />
            </div>
            <div className="flex flex-col overflow-hidden">
                <span className="font-semibold text-sm truncate">{fileName}</span>
                <span className="text-xs opacity-70 uppercase">{url.split('.').pop()} Document</span>
            </div>
            <Download className="w-4 h-4 ml-auto opacity-70" />
        </a>
    );
};

const TicketDetail = () => {
    const { ticketNumber } = useParams();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Reply state
    const [replyText, setReplyText] = useState('');
    const [files, setFiles] = useState([]);
    const [replying, setReplying] = useState(false);
    const messagesEndRef = useRef(null);

    const fetchTicket = async () => {
        setLoading(true);
        try {
            const res = await supportAPI.trackTicket(ticketNumber);
            setTicket(res.data.ticket);
            setMessages(res.data.messages);
            setActivity(res.data.activity);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch ticket');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTicket();
    }, [ticketNumber]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleFileChange = (e) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            if (files.length + newFiles.length > 3) {
                toast.error('Maximum 3 files allowed');
                return;
            }
            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim() && files.length === 0) return;
        
        setReplying(true);
        try {
            const payload = new FormData();
            payload.append('message', replyText);
            files.forEach(f => payload.append('attachments', f));

            const res = await supportAPI.customerReply(ticket._id, payload);
            setMessages(prev => [...prev, res.data.message]);
            setReplyText('');
            setFiles([]);
            toast.success('Reply sent');
            
            // Re-fetch to update status and activity if needed
            fetchTicket();
        } catch (err) {
            toast.error('Failed to send reply');
        } finally {
            setReplying(false);
        }
    };

    const handleAction = async (action) => {
        try {
            if (action === 'close') {
                await supportAPI.closeTicket(ticket._id);
                toast.success('Ticket closed');
            } else if (action === 'reopen') {
                await supportAPI.reopenTicket(ticket._id);
                toast.success('Ticket reopened');
            }
            fetchTicket();
        } catch (err) {
            toast.error(`Failed to ${action} ticket`);
        }
    };

    if (loading) return (
        <PublicLayout>
            <div className="min-h-[60vh] flex items-center justify-center"><LoadingSpinner size="lg" /></div>
        </PublicLayout>
    );

    if (error) return (
        <PublicLayout>
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                <h2 className="text-2xl font-bold text-midnight-900 mb-2">Ticket Not Found</h2>
                <p className="text-midnight-600 mb-6">{error}</p>
                <Link to="/support/track" className="btn-primary">Try Another Ticket</Link>
            </div>
        </PublicLayout>
    );

    const isClosed = ticket.status === 'Closed' || ticket.status === 'Resolved';

    return (
        <PublicLayout>
            <SEO title={`Ticket ${ticketNumber}`} noIndex />
            <div className="min-h-screen bg-midnight-50 py-8">
                <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-3 gap-8">
                    
                    {/* Main Content - Chat */}
                    <div className="lg:col-span-2 flex flex-col h-[80vh]">
                        {/* Header */}
                        <div className="card p-6 mb-4 shrink-0">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h1 className="text-2xl font-display font-bold text-midnight-900 mb-1">{ticket.subject}</h1>
                                    <p className="text-sm text-midnight-500 font-mono">{ticket.ticketNumber}</p>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[ticket.status]}`}>
                                    {ticket.status}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-midnight-600">
                                <span><strong>Category:</strong> {ticket.category}</span>
                                {ticket.bookingId && <span><strong>Booking:</strong> {ticket.bookingId}</span>}
                                {ticket.station && <span><strong>Station:</strong> {ticket.station.name}</span>}
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div className="card flex-grow overflow-hidden flex flex-col">
                            <div className="flex-grow overflow-y-auto p-6 space-y-6 bg-slate-50">
                                {messages.map((msg) => {
                                    const isCustomer = msg.senderType === 'customer';
                                    return (
                                        <div key={msg._id} className={`flex flex-col ${isCustomer ? 'items-end' : 'items-start'}`}>
                                            <div className="text-xs text-midnight-400 mb-1 px-1">
                                                {msg.senderName} • {new Date(msg.createdAt).toLocaleString('en-IN', { hour: '2-digit', minute:'2-digit', month: 'short', day: 'numeric' })}
                                            </div>
                                            <div className={`max-w-[85%] rounded-2xl px-5 py-3 shadow-sm ${isCustomer ? 'bg-railway-500 text-white rounded-tr-none' : 'bg-white border border-midnight-100 text-midnight-800 rounded-tl-none'}`}>
                                                {msg.message && <p className="whitespace-pre-wrap leading-relaxed">{msg.message}</p>}
                                                
                                                {msg.attachments?.length > 0 && (
                                                    <div className="mt-2 flex flex-col gap-2">
                                                        {msg.attachments.map((url, i) => (
                                                            <AttachmentViewer key={i} url={url} isCustomer={isCustomer} />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Reply Box */}
                            <div className="p-4 border-t border-midnight-100 bg-white">
                                {ticket.status === 'Closed' ? (
                                    <div className="text-center p-4">
                                        <p className="text-midnight-500 mb-3">This ticket has been closed.</p>
                                        <button onClick={() => handleAction('reopen')} className="btn-secondary">Reopen Ticket</button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleReply}>
                                        <div className="flex gap-2">
                                            <textarea 
                                                value={replyText}
                                                onChange={e => setReplyText(e.target.value)}
                                                placeholder="Type your reply here..."
                                                className="input-field resize-none py-3"
                                                rows="2"
                                            />
                                        </div>
                                        
                                        {/* Attachments Preview */}
                                        {files.length > 0 && (
                                            <div className="mt-2 flex gap-2 flex-wrap">
                                                {files.map((file, i) => (
                                                    <div key={i} className="flex items-center gap-1 bg-midnight-50 px-2 py-1 rounded text-xs border border-midnight-100">
                                                        <span className="truncate max-w-[100px]">{file.name}</span>
                                                        <button type="button" onClick={() => setFiles(fs => fs.filter((_, idx) => idx !== i))}><X className="w-3 h-3 text-red-500"/></button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="mt-3 flex justify-between items-center">
                                            <label className="cursor-pointer flex items-center gap-2 text-midnight-500 hover:text-railway-500 transition-colors">
                                                <Paperclip className="w-5 h-5" />
                                                <span className="text-sm">Attach files</span>
                                                <input type="file" multiple className="hidden" onChange={handleFileChange} accept=".jpg,.png,.pdf" />
                                            </label>
                                            
                                            <div className="flex gap-3">
                                                {ticket.status === 'Resolved' && (
                                                    <button type="button" onClick={() => handleAction('close')} className="btn-secondary py-2 text-sm">Close Ticket</button>
                                                )}
                                                <button type="submit" disabled={replying || (!replyText.trim() && files.length === 0)} className="btn-primary py-2 px-4 flex items-center gap-2">
                                                    {replying ? 'Sending...' : <><Send className="w-4 h-4" /> Send</>}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar - Timeline */}
                    <div className="lg:col-span-1">
                        <div className="card p-6 sticky top-24">
                            <h3 className="font-display font-bold text-lg text-midnight-900 mb-4 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-midnight-400" />
                                Activity Timeline
                            </h3>
                            
                            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-midnight-200 before:to-transparent">
                                {activity.map((act, i) => (
                                    <div key={act._id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                        {/* Icon */}
                                        <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-midnight-100 text-midnight-500 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow">
                                            <CheckCircle className="w-4 h-4" />
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-midnight-100 bg-white shadow-sm">
                                            <div className="flex items-center justify-between space-x-2 mb-1">
                                                <div className="font-bold text-midnight-900 text-sm">{act.action}</div>
                                            </div>
                                            <div className="text-xs text-midnight-500">
                                                {new Date(act.createdAt).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </PublicLayout>
    );
};

export default TicketDetail;
